import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import Modal from "./modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faEllipsis,
  faFileArrowUp,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import classnames from "classnames";
import InlineLoading from "../loader/inline";
import TextInput from "./text";
import "./video.scss";
import { GrigoraDashboardContext } from "../dashboard/context";
import { remove_prefix } from "../helpers";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";
import Pills from "./pills";
import { INPUT_LIMITS } from "../input-limits";
import Toast from "../toast/toast";
import { toast } from "react-toastify";
import { APIProviderContext } from "../api";
import { CorrectIcon } from "../../assets/ui-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { PulseLoader } from "react-spinners";
import { getVideoDimensionsOf } from "../dom";

const LibraryUI = ({
  src,
  srcMode,
  onSrcChange,
  onSrcModeChange,
  opened,
  setOpened,
  className,
}) => {
  const {
    listProjectVideos: listProjectVideosAPI,
    deleteProjectVideo: deleteProjectVideoAPI,
  } = useContext(APIProviderContext);

  const { projectID } = useContext(GrigoraDashboardContext) || {};

  const [projectVideosLoaded, setProjectVideosLoaded] = useState(false);
  const [projectVideos, setProjectVideos] = useState({});
  const [projectVideosLastEvaluatedKey, setProjectVideosLastEvaluatedKey] =
    useState({});

  const getProjectVideos = async () => {
    return await new Promise((resolve, reject) => {
      if (projectVideosLoaded && !projectVideosLastEvaluatedKey) {
        resolve();
        return;
      }

      listProjectVideosAPI(projectID, undefined, projectVideosLastEvaluatedKey)
        .then((res) => {
          var temp = {};
          res.data.Output.map((video) => {
            temp[remove_prefix(video.sk, "video-")] = video;
          });
          setProjectVideosLastEvaluatedKey(res.data.LastEvaluatedKey);
          setProjectVideos({ ...projectVideos, ...temp });
          setProjectVideosLoaded(true);
          resolve();
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const deleteProjectVideo = async (videoID) => {
    return await new Promise((resolve, reject) => {
      deleteProjectVideoAPI(projectID, videoID)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          var temp = {
            ...projectVideos,
          };
          delete temp[videoID];
          setProjectVideos(temp);
          resolve(res.data.Output.id);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.Output?.message);
          reject();
        });
    });
  };

  useEffect(() => {
    getProjectVideos().catch((err) => {
      console.log(err);
    });
    return () => {
      setProjectVideosLoaded(false);
      setProjectVideos({});
      setProjectVideosLastEvaluatedKey({});
    };
  }, []);

  if (!projectVideosLoaded) {
    return <InlineLoading />;
  }
  const classes = classnames(
    "grigora-videos-library-main grigora-custom-scrollbar flex flex-col justify-start select-none overflow-auto gap-7 mt-5 bg-ui-light-white h-[90vh] rounded-[21px] p-[10px]",
    className,
  );

  return (
    <div id="grigora-videos-library-scrollable-area" className={classes}>
      {projectVideosLoaded ? (
        projectVideos && Object.values(projectVideos).length > 0 ? (
          <>
            <h1 className="modal-heading">
              Choose from the library
              <br />
            </h1>
            <InfiniteScroll
              dataLength={Object.values(projectVideos)?.length}
              next={() => getProjectVideos()}
              hasMore={projectVideosLastEvaluatedKey !== undefined}
              scrollableTarget="grigora-videos-library-scrollable-area"
              loader={
                <div className="flex justify-center m-2">
                  <PulseLoader size={6} color="currentColor" loading={true} />
                </div>
              }
            >
              <div className="video-grid">
                {[
                  ...(srcMode === "cdn" && src ? [src] : []), // if srcMode is cdn and src is not undefined, then add src to the list of videos
                  ...Object.values(projectVideos).filter((item) => {
                    if (srcMode === "cdn" && src) {
                      return item.sk !== src.sk;
                    } else {
                      return true;
                    }
                  }),
                ].map((item) => {
                  return (
                    <VideoComponent
                      id={projectID}
                      key={item.name}
                      item={item}
                      src={src}
                      onSrcChange={onSrcChange}
                      srcMode={srcMode}
                      onSrcModeChange={onSrcModeChange}
                      opened
                      setOpened={setOpened}
                      deleteProjectVideo={deleteProjectVideo}
                    />
                  );
                })}
              </div>
            </InfiniteScroll>
          </>
        ) : (
          <>
            <div className="flex flex-col justify-center items-center w-full h-full bg-ui-light-white rounded-[21px] mt-4 mx-auto my-auto">
              <p className="text-grigora-base text-gray-700">No videos found</p>
            </div>
          </>
        )
      ) : (
        <PulseLoader size={6} color="currentColor" />
      )}
    </div>
  );
};

const VideoComponent = (props) => {
  const {
    id,
    item,
    src,
    srcMode,
    onSrcChange,
    onSrcModeChange,
    opened,
    setOpened,
    deleteProjectVideo,
  } = props;

  const [openPopOver, setOpenPopOver] = useState(false);

  const { projectID } = useContext(GrigoraDashboardContext);

  var url;
  url = `https://cdn.grigora.co/projects/${id}/${remove_prefix(
    item.sk,
    "video-",
  )}`;

  var name = useMemo(() => {
    // Split the sk by '/' and get the last part of the string
    var name = item.sk.split("/").pop();
    // Remove the prefix 'video-' from the name
    name = remove_prefix(name, "video-");

    return name;
  });

  return (
    <div
      className={`video-component border-[5px] border-solid border-transparent ${
        item?.sk === src?.sk ? "!border-ui-dark-blue" : ""
      } min-w-[264px] h-[174px] cursor-pointer rounded-[21px] relative overflow-hidden`}
    >
      <div
        onClick={() => {
          onSrcModeChange("cdn");
          onSrcChange({
            ...item,
            projectID: item.projectID ? item.projectID : projectID,
          });
          setOpened(false);
        }}
        className="bg-gray-100 hover:bg-gray-200 duration-300 object-cover h-full w-full text-grigora-base flex items-center justify-center text-center p-2"
      >
        {name}
      </div>
      {/* <video
          onClick={() => {
            onSrcModeChange("cdn");
            onSrcChange({
              ...item,
              projectID: item.projectID ? item.projectID : projectID,
            });
            setOpened(false);
          }}
          preload="metadata"
          className="object-cover h-full w-full"
        >
          <source src={url} />
        </video> */}

      <Popover
        placement="left"
        open={openPopOver}
        onOpenChange={setOpenPopOver}
      >
        <PopoverAnchor
          onClick={(e) => {
            setOpenPopOver(!openPopOver);
          }}
          className={`library-ui-options ${
            openPopOver ? "active" : ""
          } duration-300 absolute top-1 right-2 rotate-90 w-[20px] h-[20px] overflow-hidden !rounded-full`}
        >
          <div
            style={{
              background: openPopOver ? "#623CE7" : undefined,
            }}
            className=" hover:bg-[#623CE7] duration-300 flex items-center justify-center w-full h-full bg-slate-700"
          >
            <FontAwesomeIcon color="#fff" size="sm" icon={faEllipsis} />
          </div>
        </PopoverAnchor>
        <PopoverContent className={"video-thumbnail p-1 flex !z-[1000000]"}>
          <div
            onClick={(e) => {
              deleteProjectVideo(remove_prefix(item.sk, "video-"))
                .then((data) => {
                  setOpenPopOver(false);
                })
                .catch((err) => {
                  console.log(err);
                  setOpenPopOver(false);
                });
            }}
            className="bg-ui-light-white cursor-pointer duration-300 hover:bg-[#623CE7] rounded-[21px] p-1 px-2 w-24 flex justify-between items-center text-black hover:text-white"
          >
            <span className="text-sm">Delete</span>
            <FontAwesomeIcon icon={faTrashCan} size="sm" />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

function VideoPickerUIKit({
  src,
  srcMode,
  onSrcChange,
  onSrcModeChange,
  opened,
  setOpened,
}) {
  const {} = useContext(APIProviderContext) || {};

  const [activeTab, setActiveTab] = useState(srcMode ? srcMode : "upload");

  const { projectID, uploadProjectVideo } =
    useContext(GrigoraDashboardContext) ?? {};

  const handleFileInput = (e) => {
    const url = URL.createObjectURL(e.target.files[0]);
    onSrcChange(url);
    onSrcModeChange("preview");
    setOpened(false);

    if (e.target.files[0]) {
      uploadProjectVideo &&
        uploadProjectVideo(e.target.files[0])
          .then((newVideo) => {
            onSrcChange({
              ...newVideo,
              projectID: newVideo.projectID ? newVideo.projectID : projectID,
            });
            onSrcModeChange("cdn");
          })
          .catch((err) => {
            onSrcChange(undefined);
            onSrcModeChange(undefined);
          });
    }
  };

  const inputFileRef = useRef();

  function Upload() {
    return (
      <div
        onClick={() => {
          inputFileRef.current.click();
        }}
        className="flex flex-col justify-center items-center w-full h-full bg-ui-light-white gap-5 rounded-[21px] mt-4 mx-auto my-auto"
      >
        <h1 className="modal-heading">Drag and drop your file here</h1>
        <span className="modal-heading">or</span>
        <button className="grigora-action-btn">Upload from Computer</button>
        <input
          className="hidden"
          type="file"
          ref={inputFileRef}
          onChange={handleFileInput}
          accept="video/*"
        ></input>
      </div>
    );
  }
  function Library() {
    return (
      <LibraryUI
        src={src}
        srcMode={srcMode}
        onSrcChange={onSrcChange}
        onSrcModeChange={onSrcModeChange}
        opened
        setOpened={setOpened}
      />
    );
  }

  const UrlUI = ({
    src,
    srcMode,
    onSrcChange,
    onSrcModeChange,
    opened,
    setOpened,
  }) => {
    const [link, setLink] = useState(srcMode === "link" ? src : "");
    const linkRef = useRef();

    const linkCheck = () => {
      const urlPattern =
        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
      if (urlPattern.test(link)) {
        linkRef.current.innerText = "";
        return true;
      } else {
        linkRef.current.innerText = "Not a valid link";
        return false;
      }
    };

    const handleSave = (e) => {
      e.preventDefault();
      if (linkCheck()) {
        onSrcChange(link);
        onSrcModeChange("link");
        setOpened(false);
      }
    };

    return (
      <div className="flex flex-col justify-center items-center w-full h-full bg-ui-light-white rounded-[21px] mt-4 mx-auto my-auto">
        <h1 className="modal-heading">Import a file from URL</h1>
        <input
          className="rounded-[21px] min-w-[376px] h-[48px] outline-none px-[20px] text-sm pt-2 pb-2 pl-[30px] pr-[30px] mt-[30px] font-normal bg-white btn-shadow "
          type="text"
          minLength={INPUT_LIMITS["outbound-link"].minlength}
          maxLength={INPUT_LIMITS["outbound-link"].maxlength}
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Paste a link here..."
        ></input>
        <div className="text-sm mt-4 text-red-500" ref={linkRef}></div>
        <button onClick={handleSave} className="grigora-action-btn">
          Save
        </button>
      </div>
    );
  };
  function Url() {
    return (
      <>
        <UrlUI
          src={src}
          srcMode={srcMode}
          onSrcChange={onSrcChange}
          onSrcModeChange={onSrcModeChange}
          opened
          setOpened={setOpened}
        />
      </>
    );
  }

  const options = [
    {
      label: "Upload",
      key: "upload",
    },
    {
      label: "Library",
      key: "cdn",
    },
    {
      label: "URL",
      key: "url",
    },
  ];

  return (
    <>
      <div>
        <Modal
          isOpened={opened}
          onOutsideClick={() => {
            setOpened(false);
          }}
        >
          <div className="bg-white rounded-[21px] flex flex-col mw-[90vw] mh-[90vh] w-[66vw] h-[90vh] p-5 cursor-default">
            <div className=" flex items-center justify-between">
              <div className="flex items-center gap-5">
                <h1 className="modal-heading">Upload</h1>
                <div className="flex gap-5">
                  <Pills
                    options={options}
                    isActive={activeTab}
                    setIsActive={setActiveTab}
                  />
                </div>
              </div>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpened(false);
                }}
                className="p-2 grid cursor-pointer"
              >
                <FontAwesomeIcon icon={faClose} />
              </span>
            </div>
            {/* content */}
            {activeTab === "upload"
              ? Upload()
              : activeTab === "cdn"
              ? Library()
              : Url()}
          </div>
        </Modal>
      </div>

      {/* <div>
      <Modal
        isOpened={opened}
        onOutsideClick={() => {
          setOpened(false);
        }}
      >
        <div className="grigora-video-picker flex flex-col mw-[80vw] mh-[80vh] w-[50vw] h-[60vh] p-5 cursor-default">
          <div className="grigora-video-picker__title flex items-center justify-between">
            Choose an Video
            <span
              onClick={(e) => {
                e.stopPropagation();
                setOpened(false);
              }}
              className="p-2 grid cursor-pointer hover:bg-editor-bg-main-hover"
            >
              <FontAwesomeIcon icon={faClose} />
            </span>
          </div>
          <Tabs initialOpenIndex={srcMode === "cdn" ? 1 : (srcMode === "url" ? 2 : undefined)} items={tabs} />
        </div>
      </Modal>
    </div> */}
    </>
  );
}

export default VideoPickerUIKit;
